import { useEffect, useState } from "react";

// §1.1 — age gate. One screen, one button. Declaration only, no verification.
// Cookie: mc_age_ack=1, 180-day Max-Age (concept: "not repeated on return
// visits from the same device"). Device-local, no fingerprinting.
// Skipped on /admin (admins have their own login).

const COOKIE = "mc_age_ack";
const MAX_AGE = 60 * 60 * 24 * 180; // 180 days

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function writeCookie(name: string, value: string) {
  const secure = typeof window !== "undefined" && window.location.protocol === "https:";
  document.cookie =
    `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${MAX_AGE}; SameSite=Lax` +
    (secure ? "; Secure" : "");
}

// Per-device, locale-independent acknowledgement. We store the same signal
// in BOTH a cookie (Path=/, locale-independent) AND localStorage as a
// belt-and-braces fallback — some preview iframes / partitioned storage
// modes drop one but keep the other. Either one being set counts as ack.
function hasAck(): boolean {
  if (typeof window === "undefined") return false;
  if (readCookie(COOKIE) === "1") return true;
  try {
    return window.localStorage.getItem(COOKIE) === "1";
  } catch {
    return false;
  }
}

function writeAck() {
  writeCookie(COOKIE, "1");
  try {
    window.localStorage.setItem(COOKIE, "1");
  } catch {
    /* private mode / partitioned storage — cookie still set */
  }
}

type Copy = {
  title: string;
  body: string;
  cta: string;
};

const COPY: Record<string, Copy> = {
  en: {
    title: "This site is for adults.",
    body:
      "This site contains first-hand testimony from seafarers, including strong language. We do not censor the authors' words — to do so would be dishonest to their experience. By entering, you confirm you are 18 or older.",
    cta: "I am 18 or older — Enter",
  },
  ru: {
    title: "Сайт для взрослых.",
    body:
      "На этом сайте — свидетельства моряков из первых уст, в том числе с крепкой речью. Мы не сглаживаем слова авторов — это было бы нечестно по отношению к их опыту. Входя, вы подтверждаете, что вам исполнилось 18 лет.",
    cta: "Мне 18 или больше — войти",
  },
};

export function AgeGate({ lang }: { lang: string }) {
  // Three states: SSR/pre-mount (hidden, avoid hydration flicker), ack'd
  // (hidden), not-ack'd (show gate). We re-check on every mount, so
  // locale switches that remount the layout will still see the cookie.
  const [mounted, setMounted] = useState(false);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    setAck(hasAck());
    setMounted(true);
  }, []);

  if (!mounted || ack) return null;

  const copy = COPY[lang] ?? COPY.en;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-6"
      style={{ background: "color-mix(in oklab, var(--ink) 92%, transparent)" }}
    >
      <div
        className="w-full max-w-lg rounded-sm border p-8 shadow-2xl"
        style={{ background: "var(--paper, var(--background))", borderColor: "var(--amber)" }}
      >
        <p
          className="mb-3 text-[11px] uppercase tracking-[0.22em]"
          style={{ color: "var(--amber)" }}
        >
          MadCaptains.net
        </p>
        <h1 id="age-gate-title" className="font-display text-3xl leading-tight">
          {copy.title}
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{copy.body}</p>
        <button
          type="button"
          onClick={() => {
            writeAck();
            setAck(true);
          }}
          className="mt-7 w-full rounded-sm px-5 py-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2"
          style={{
            background: "var(--ink, var(--foreground))",
            color: "var(--paper, var(--background))",
            outlineColor: "var(--amber)",
          }}
        >
          {copy.cta}
        </button>
      </div>
    </div>
  );
}